/**
 * EstimateApprovalControl - Approve / revoke the estimate on a Work
 * Order. Inspections have no estimate, so they never render this.
 *
 * Two approver paths surfaced on the same control:
 *   - "Approve as Super Admin"   : visible only to super_admins. Single
 *                                  click; records the user as the approver.
 *   - "Customer approval"        : visible to any user with edit
 *                                  permission. Opens a small inline
 *                                  modal that lets the manager pick
 *                                  the on-site contact who is signing
 *                                  off, then records the approval
 *                                  against that contact's id.
 *
 * When the estimate is already approved, the control instead shows
 * who approved + when, plus a "Revoke" button (super_admin or any
 * company-tier user — server enforces the latter scope).
 *
 * The component is purely a UI shell over estimateApprovalService.
 * It owns no data fetching — the parent passes the current approval
 * state and the contacts list, and receives notification of state
 * changes via `onChange()` so the parent can refetch the parent
 * record.
 */
import React, { useState } from 'react';
import { Button } from '../../atoms';
import { Modal } from '../Modal';
import { useAuth } from '../../../hooks/useAuth';
import {
    estimateApprovalService,
    EstimateApproverType,
} from '../../../services/estimateApprovalService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Subset of the Contact shape we need for the picker dropdown.
 * Parent typically maps from the company's contact list it already
 * loads for the ContactFieldset on the same detail page.
 */
export interface EstimateApprovalContactOption {
    id: number;
    name: string;
}

/**
 * Snapshot of the parent record's approval state — drives whether
 * the control renders the "approve" affordance or the "approved by
 * X on Y" + revoke affordance.
 */
export interface EstimateApprovalState {
    approvedAt: string | null;
    approvedByUserName: string | null;
    approvedByContactName: string | null;
}

/**
 * Props for EstimateApprovalControl.
 *
 * @property recordId    The id of the work order being approved
 * @property approval    Current approval state (or all-null when unapproved)
 * @property contacts    Company contacts available for "customer approval"
 * @property canApprove  Whether the current user can approve at all
 *                       (parent computes this — typically super_admin OR
 *                       admin/manager/company_admin on the parent's company)
 * @property onChange    Called after a successful approve/revoke so the
 *                       parent can refetch the parent record
 * @property className   Optional extra classes for the outer wrapper
 */
export interface EstimateApprovalControlProps {
    recordId: string;
    approval: EstimateApprovalState;
    contacts: EstimateApprovalContactOption[];
    canApprove: boolean;
    onChange: () => void;
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const EstimateApprovalControl: React.FC<EstimateApprovalControlProps> = ({
    recordId,
    approval,
    contacts,
    canApprove,
    onChange,
    className,
}) => {
    /** Used to gate the "Approve as Super Admin" button. */
    const { hasRole } = useAuth();
    const isSuperAdmin = hasRole('super_admin');

    /* Local UI state — modal visibility, selected contact, pending flags. */
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState<string>('');
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /** Posts the approval, then notifies the parent on success. */
    const callApprove = async (approverType: EstimateApproverType, contactId?: number) => {
        setIsPending(true);
        setError(null);
        try {
            await estimateApprovalService.approveWorkOrder(recordId, { approverType, contactId });
            onChange();
            setIsCustomerModalOpen(false);
            setSelectedContactId('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to approve');
        } finally {
            setIsPending(false);
        }
    };

    /** Same shape but for revocation. */
    const callRevoke = async () => {
        if (!window.confirm('Revoke estimate approval? The record will be marked as unapproved.')) return;
        setIsPending(true);
        setError(null);
        try {
            await estimateApprovalService.revokeWorkOrder(recordId);
            onChange();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to revoke');
        } finally {
            setIsPending(false);
        }
    };

    const isApproved = approval.approvedAt !== null;
    const approverDisplayName = approval.approvedByUserName || approval.approvedByContactName;

    /* ---------------------------------------------------------------
     * Render — approved branch
     * --------------------------------------------------------------- */
    if (isApproved) {
        return (
            <div className={['estimate-approval-control', 'estimate-approval-control--approved', 'flex flex-wrap items-center gap-12px', className ?? ''].join(' ')} data-ref="organism-estimate-approval-control">
                <div className="estimate-approval-control__summary text-13px text-brand-text">
                    <span className="font-medium text-green-700">Approved</span>
                    {approverDisplayName && (
                        <> by <span className="font-medium">{approverDisplayName}</span></>
                    )}
                    {approval.approvedAt && (
                        <> on <span>{new Date(approval.approvedAt).toLocaleDateString()}</span></>
                    )}
                </div>
                {canApprove && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={callRevoke}
                        isLoading={isPending}
                    >
                        Revoke
                    </Button>
                )}
                {error && <span className="text-12px text-red-700">{error}</span>}
            </div>
        );
    }

    /* ---------------------------------------------------------------
     * Render — unapproved branch
     * --------------------------------------------------------------- */
    if (!canApprove) {
        // No editing permission — just say "Not approved" so the field
        // is visible without offering an action the user can't take.
        return (
            <div className={['estimate-approval-control', 'text-13px text-brand-text-muted', className ?? ''].join(' ')} data-ref="organism-estimate-approval-control">
                Not approved
            </div>
        );
    }

    return (
        <div className={['estimate-approval-control', 'flex flex-wrap items-center gap-8px', className ?? ''].join(' ')} data-ref="organism-estimate-approval-control">
            {isSuperAdmin && (
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => callApprove('super_admin')}
                    isLoading={isPending}
                >
                    Approve as Super Admin
                </Button>
            )}
            <Button
                variant={isSuperAdmin ? 'secondary' : 'primary'}
                size="sm"
                onClick={() => setIsCustomerModalOpen(true)}
                disabled={isPending}
            >
                Customer Approval
            </Button>
            {error && <span className="text-12px text-red-700">{error}</span>}

            {/* Customer approval modal — pick the contact who is approving */}
            <Modal
                isOpen={isCustomerModalOpen}
                onClose={() => setIsCustomerModalOpen(false)}
                title="Customer estimate approval"
                size="sm"
            >
                <div className="estimate-approval-control__form space-y-12px">
                    <p className="text-13px text-brand-text-muted">
                        Select the customer contact who is approving this estimate.
                    </p>
                    {contacts.length === 0 ? (
                        <div className="estimate-approval-control__note text-13px text-brand-text-muted px-12px py-8px bg-brand-surface border border-brand-border rounded-md">
                            No contacts on file for this company. Add a contact in the
                            Contacts section first, then return here.
                        </div>
                    ) : (
                        <select
                            value={selectedContactId}
                            onChange={(e) => setSelectedContactId(e.target.value)}
                            className="block w-full rounded-md border border-brand-border px-12px py-10px text-16px desktop:text-14px font-sans focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white"
                        >
                            <option value="">Choose a contact…</option>
                            {contacts.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    )}

                    <div className="estimate-approval-control__actions flex justify-end gap-8px pt-8px">
                        <Button
                            variant="ghost"
                            onClick={() => setIsCustomerModalOpen(false)}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => selectedContactId && callApprove('contact', Number(selectedContactId))}
                            disabled={!selectedContactId || contacts.length === 0}
                            isLoading={isPending}
                        >
                            Record Approval
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EstimateApprovalControl;
